// 📄 pages/Proyectos/components/CasosPruebaSection.tsx
// "Casos de Prueba" (Funcionalidades): biblioteca de escenarios de prueba
// funcional por solución/canal, con datos de prueba libres (ver
// casoPruebaService.ts) — no reemplaza el checklist técnico de integración
// (SPRO/transacciones SAP), es la contraparte funcional: "qué hay que
// probar", no "cómo se configura".
import React, { useEffect, useMemo, useState } from 'react';
import { clasificacionDe } from '../modulosConfig.ts';
import { FlaskConical, Plus, RefreshCw, Library } from 'lucide-react';
import { useProject } from '../../../context/ProjectContext.tsx';
import { useAuth } from '../../../context/AuthContext.tsx';
import {
  CasoPrueba, CasoPruebaEstado, CASO_PRUEBA_ESTADOS, DetalleAprobacion, DetalleFallo,
  getCasosPrueba, updateCasoPruebaEstado, linkIncidenteACaso
} from '../../../services/casoPruebaService.ts';
import { SOLUCIONES } from '../solucionesConfig.ts';
import { canalesDe } from './CasoPruebaFormModal.tsx';
import CasoPruebaCard from './CasoPruebaCard.tsx';
import CasoPruebaFormModal from './CasoPruebaFormModal.tsx';
import CasoPruebaResultadoModal from './CasoPruebaResultadoModal.tsx';
import IncidenteFormModal from './IncidenteFormModal.tsx';
import CasoPruebaBibliotecaModal from './CasoPruebaBibliotecaModal.tsx';

export default function CasosPruebaSection() {
  const { project, projectId, esCliente, esAdminObservador, esOrgAdminObservador } = useProject();
  const { user, profile } = useAuth();
  const [casos, setCasos] = useState<CasoPrueba[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroSolucion, setFiltroSolucion] = useState('todas');
  const [filtroCanal, setFiltroCanal] = useState('todos');
  const [filtroEstado, setFiltroEstado] = useState<CasoPruebaEstado | 'todos'>('todos');
  const [formOpen, setFormOpen] = useState(false);
  const [editando, setEditando] = useState<CasoPrueba | null>(null);
  const [resultadoPendiente, setResultadoPendiente] = useState<{ caso: CasoPrueba; estado: 'Aprobado' | 'Fallido' } | null>(null);
  const [casoParaIncidente, setCasoParaIncidente] = useState<CasoPrueba | null>(null);
  const [bibliotecaOpen, setBibliotecaOpen] = useState(false);

  // Con SAP Ariba se clasifica por solución; sin SAP, por área del proyecto (ver modulosConfig.ts).
  const { modoArea, opciones: solucionesActivas } = clasificacionDe(project);
  // Cambiar el estado (ejecutar el caso) sigue abierto a todo el equipo —
  // restringirlo también dejaría el caso inutilizable para el resto (ver
  // firestore.rules). Editar el CONTENIDO (nombre/datos/detalles) es más
  // angosto: solo quien lo creó, o admin/owner-admin de org como excepción
  // de seguridad — a pedido explícito del usuario, para evitar que alguien
  // más lo modifique sin necesidad e interrumpa el proceso de otro.
  const puedeCambiarEstado = !esCliente && !esAdminObservador;
  const puedeEditarContenido = (caso: CasoPrueba): boolean =>
    puedeCambiarEstado && (profile?.role === 'admin' || esOrgAdminObservador || caso.creadoPorUid === user?.uid);

  const loadCasos = async () => {
    setLoading(true);
    try {
      setCasos(await getCasosPrueba(projectId));
    } catch (err) {
      console.error('Error cargando casos de prueba:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) loadCasos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const opcionesCanalFiltro = useMemo(
    () => (filtroSolucion === 'todas' ? [] : canalesDe(filtroSolucion)),
    [filtroSolucion]
  );

  const filtrados = useMemo(() => {
    return casos.filter((c) => {
      if (filtroSolucion !== 'todas' && c.solucionRelacionada !== filtroSolucion) return false;
      if (filtroCanal !== 'todos' && (c.canalId || '') !== filtroCanal) return false;
      if (filtroEstado !== 'todos' && c.estado !== filtroEstado) return false;
      return true;
    });
  }, [casos, filtroSolucion, filtroCanal, filtroEstado]);

  // Pendiente es el único cambio "en seco" — Aprobado/Fallido abren
  // CasoPruebaResultadoModal para pedir el detalle de la ejecución antes
  // de confirmar (ver handleConfirmarResultado).
  const handleEstadoChange = (caso: CasoPrueba, estado: CasoPruebaEstado) => {
    if (estado === 'Pendiente') {
      commitEstado(caso, estado);
    } else {
      setResultadoPendiente({ caso, estado });
    }
  };

  const commitEstado = async (caso: CasoPrueba, estado: CasoPruebaEstado, detalle?: DetalleAprobacion | DetalleFallo) => {
    if (!user || !project) return;
    setCasos((prev) => prev.map((c) => (c.id === caso.id ? { ...c, estado } : c)));
    try {
      await updateCasoPruebaEstado(
        projectId, caso.id, caso.nombre, estado, user.uid, profile?.name || user.email || '', project.miembros, detalle
      );
    } catch (err) {
      console.error('Error actualizando estado del caso de prueba:', err);
      loadCasos();
    }
  };

  const handleConfirmarResultado = async (detalle: DetalleAprobacion | DetalleFallo) => {
    if (!resultadoPendiente) return;
    await commitEstado(resultadoPendiente.caso, resultadoPendiente.estado, detalle);
    setResultadoPendiente(null);
  };

  const handleSaved = () => {
    setFormOpen(false);
    setEditando(null);
    loadCasos();
  };

  const handleIncidenteCreado = async (incidenteId: string) => {
    if (!casoParaIncidente) return;
    try {
      await linkIncidenteACaso(projectId, casoParaIncidente.id, incidenteId);
    } catch (err) {
      console.error('Error vinculando el incidente al caso de prueba:', err);
    }
    loadCasos();
  };

  if (!project) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value as CasoPruebaEstado | 'todos')}
            className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
          >
            <option value="todos">Todos los estados</option>
            {CASO_PRUEBA_ESTADOS.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>

          <select
            value={filtroSolucion}
            onChange={(e) => { setFiltroSolucion(e.target.value); setFiltroCanal('todos'); }}
            className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
          >
            <option value="todas">{modoArea ? 'Todas las áreas' : 'Todas las soluciones'}</option>
            {solucionesActivas.map((s) => <option key={s} value={s}>{SOLUCIONES[s]?.label || s}</option>)}
          </select>

          {opcionesCanalFiltro.length > 0 && (
            <select
              value={filtroCanal}
              onChange={(e) => setFiltroCanal(e.target.value)}
              className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
            >
              <option value="todos">Todos los canales</option>
              {opcionesCanalFiltro.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          )}
        </div>

        {puedeCambiarEstado && (
          <div className="flex items-center gap-2">
            {!modoArea && <button
              onClick={() => setBibliotecaOpen(true)}
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-2 rounded-xl text-sm font-semibold text-gray-300 transition-all"
            >
              <Library className="w-4 h-4" /> Agregar desde biblioteca
            </button>}
            <button
              onClick={() => { setEditando(null); setFormOpen(true); }}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all"
            >
              <Plus className="w-4 h-4" /> Nuevo caso de prueba
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      ) : filtrados.length === 0 ? (
        <div className="p-10 text-center text-gray-500 flex flex-col items-center gap-2">
          <FlaskConical className="w-8 h-8 text-gray-600" />
          <p className="text-sm">Todavía no hay casos de prueba{(filtroSolucion !== 'todas' || filtroEstado !== 'todos') ? ' para este filtro' : ''}.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtrados.map((caso) => (
            <CasoPruebaCard
              key={caso.id}
              caso={caso}
              projectId={projectId}
              puedeEditarContenido={puedeEditarContenido(caso)}
              puedeCambiarEstado={puedeCambiarEstado}
              onEditar={() => { setEditando(caso); setFormOpen(true); }}
              onEstadoChange={(estado) => handleEstadoChange(caso, estado)}
              onCrearIncidente={() => setCasoParaIncidente(caso)}
            />
          ))}
        </div>
      )}

      {formOpen && (
        <CasoPruebaFormModal
          projectId={projectId}
          solucionesActivas={solucionesActivas}
          modoArea={modoArea}
          caso={editando}
          onClose={() => { setFormOpen(false); setEditando(null); }}
          onSaved={handleSaved}
        />
      )}

      {resultadoPendiente && (
        <CasoPruebaResultadoModal
          caso={resultadoPendiente.caso}
          estado={resultadoPendiente.estado}
          onClose={() => setResultadoPendiente(null)}
          onConfirm={handleConfirmarResultado}
        />
      )}

      {casoParaIncidente && (
        <IncidenteFormModal
          projectId={projectId}
          solucionesActivas={solucionesActivas}
          modoArea={modoArea}
          miembros={project.miembros}
          prefill={{
            titulo: casoParaIncidente.nombre,
            descripcion: casoParaIncidente.detalleFallo?.descripcion || '',
            solucionRelacionada: casoParaIncidente.solucionRelacionada,
            fase: 'Proyecto',
            casoPruebaId: casoParaIncidente.id
          }}
          onCreated={handleIncidenteCreado}
          onClose={() => setCasoParaIncidente(null)}
          onSaved={() => setCasoParaIncidente(null)}
        />
      )}

      {bibliotecaOpen && user && (
        <CasoPruebaBibliotecaModal
          projectId={projectId}
          solucionesActivas={solucionesActivas}
          creadoPorUid={user.uid}
          creadoPorNombre={profile?.name || user.email || ''}
          onClose={() => setBibliotecaOpen(false)}
          onAgregados={() => { setBibliotecaOpen(false); loadCasos(); }}
        />
      )}
    </div>
  );
}
